import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { PDFDocumentLoadingTask, PDFPageProxy, RenderTask } from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { scrollForCenter, viewCenter, type ViewMemory } from './viewState'

export type PageRegion={x:number;y:number;width:number;height:number};
export function PdfSource({ url, page, zoom, fitPage = false, label, highlight, precision='exact', focusArea, marker, markers=[], viewMemory, active = true, onPainted, focusRequest = 0, focusRegion = false, crop, selecting=false, selection, selectRegion }: {onPainted?:()=>void;viewMemory?:ViewMemory;active?:boolean;precision?:'exact'|'approximate';marker?:{tone:'amber'|'blue';label:string;precision?:'exact'|'approximate'|'unresolved'};markers?:(PageRegion&{key:string;precision?:'exact'|'approximate'})[];crop?:PageRegion;selecting?:boolean;selection?:PageRegion|null;selectRegion?:(region:PageRegion)=>void; focusRegion?:boolean; focusRequest?:number; url: string; page: number; zoom: number; fitPage?: boolean; label?: string; highlight?: PageRegion; focusArea?: PageRegion }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const previous = useRef({ width: 0, height: 0, left: 0, top: 0, viewport: {width: 0, height: 0}, url: '', page: -1 })
  const previousFocus = useRef('')
  const wasActive = useRef(active)
  const reportedPaint = useRef('')
  // The scroll listener is attached once; it reads the current page geometry from here.
  const geometry = useRef({ identity: '', ready: false, active, viewport: { width: 0, height: 0 }, page: { width: 0, height: 0, left: 0, top: 0 } })
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null)
  const selectionStart=useRef<{x:number;y:number}|null>(null)
  const identity = `${url}#${page}`
  const [loadedPage, setLoadedPage] = useState<{identity:string;page:PDFPageProxy} | null>(null)
  const [painted, setPainted] = useState('')
  const pdfPage = loadedPage?.identity === identity ? loadedPage.page : null
  const visible = !!pdfPage && painted === identity
  const [error, setError] = useState('')
  const [size, setSize] = useState({ width: 500, height: 360 })
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  useEffect(() => {
    let disposed = false
    let task: PDFDocumentLoadingTask | null = null
    setError('')
    void import('pdfjs-dist').then(async pdfjs => {
      if (disposed) return
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
      task = pdfjs.getDocument({ url, withCredentials: true })
      const document = await task.promise
      const loaded = await document.getPage(page + 1)
      if (!disposed) setLoadedPage({identity,page:loaded})
    }).catch(error => { if (!disposed) setError(String(error)) })
    return () => { disposed = true; void task?.destroy() }
  }, [url, page, identity])

  useEffect(() => {
    const host = wrapper.current?.parentElement
    if (!host) return
    let frame = 0
    const scroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        setOffset({ x: host.scrollLeft, y: host.scrollTop })
        const g = geometry.current
        // Only the viewer the operator is using records the position; the one under the
        // full-screen dialog keeps following the shared scale and must not overwrite it.
        if (viewMemory && g.ready && g.active) {
          const center = viewCenter({ left: host.scrollLeft, top: host.scrollTop }, g.viewport, g.page)
          viewMemory.current = center && { identity: g.identity, ...center }
        }
      })
    }
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      setSize({ width: Math.max(1, Math.min(width, window.innerWidth)),
        height: Math.max(1, Math.min(height, window.innerHeight)) })
    })
    observer.observe(host)
    host.addEventListener('scroll', scroll, { passive: true })
    return () => { observer.disconnect(); host.removeEventListener('scroll', scroll); cancelAnimationFrame(frame) }
  }, [])

  // The opening view covers the whole row when the caller knows it; the highlight marks the value.
  const focus = focusArea ?? highlight
  const fullUnit = pdfPage?.getViewport({ scale: 1 })
  const unit = fullUnit ? {width:fullUnit.width*(crop?.width??1),height:fullUnit.height*(crop?.height??1)} : null
  const fitScale = unit ? (fitPage ? Math.min(size.width / unit.width, size.height / unit.height)
    : focusRegion && focusRequest && focus ? Math.min(size.width / (unit.width * Math.max(.12, focus.width * 1.3)),
      size.height / (unit.height * Math.max(.12, focus.height * 2))) : size.width / unit.width) : 1
  const scale = fitScale * zoom
  const width = unit ? unit.width*scale : size.width
  const height = unit ? unit.height*scale : size.height
  const left = fitPage ? Math.max(0, (size.width - width) / 2) : 0
  const top = fitPage ? Math.max(0, (size.height - height) / 2) : 0
  geometry.current = { identity, ready: !!pdfPage, active, viewport: size, page: { width, height, left, top } }
  useLayoutEffect(() => {
    const host = wrapper.current?.parentElement
    if (!host || !pdfPage) return
    const old = previous.current
    const focusIdentity = `${identity}:${focusRequest}:${focus?.x}:${focus?.y}:${focus?.width}`
    const opened = old.url !== url || old.page !== page
    const remembered = viewMemory?.current?.identity === identity ? viewMemory.current : null
    const returned = active && !wasActive.current
    wasActive.current = active
    if (remembered && (opened || returned) && !(fitPage && zoom === 1)) {
      // Same page seen in another viewer (full screen ↔ inline): keep the point the operator was on.
      const target = scrollForCenter(remembered, size, { width, height, left, top })
      host.scrollTo(target.left, target.top)
      previousFocus.current = focusIdentity
    } else if (focusRequest && focus && previousFocus.current !== focusIdentity) {
      // Fit the complete row horizontally: the mark and column headings must
      // remain accessible, not just an isolated quantity cell.
      host.scrollTo(focusRegion ? (focus.x + focus.width / 2) * width - size.width / 2 : 0,
        (focus.y + focus.height / 2) * height - size.height / 2)
      previousFocus.current = focusIdentity
    } else if (opened || (fitPage && zoom === 1)) {
      host.scrollTo(0, 0)
    } else if (old.width && (old.width !== width || old.viewport.width !== size.width || old.viewport.height !== size.height)) {
      const center = viewCenter({left:host.scrollLeft,top:host.scrollTop}, old.viewport, old)
      if (center) {
        const target = scrollForCenter(center, size, {width,height,left,top})
        host.scrollTo(target.left, target.top)
      }
    }
    previous.current = { width, height, left, top, viewport: size, url, page }
    setOffset({ x: host.scrollLeft, y: host.scrollTop })
  }, [width, height, left, top, pdfPage, url, page, size.width, size.height, fitPage, zoom, focusRequest, focusRegion, identity, focus?.x, focus?.y, focus?.width, focus?.height, active, viewMemory])

  useEffect(() => {
    if (!pdfPage || !canvas.current) return
    let disposed = false
    const viewport = pdfPage.getViewport({ scale })
    const density = Math.min(window.devicePixelRatio || 1, 2)
    const target = canvas.current
    // Allocate pixels only for the visible area, including at maximum CAD zoom.
    target.width = Math.ceil(Math.min(size.width, width) * density)
    target.height = Math.ceil(Math.min(size.height, height) * density)
    target.style.width = `${target.width / density}px`
    target.style.height = `${target.height / density}px`
    const render: RenderTask = pdfPage.render({ canvas: target, viewport,
      transform: [density, 0, 0, density, -(offset.x+viewport.width*(crop?.x??0)) * density, -(offset.y+viewport.height*(crop?.y??0)) * density] })
    void render.promise.then(()=>{
      if(disposed)return
      setPainted(identity)
      // First paint of this page: the caller measures how long the operator waited for it.
      if(reportedPaint.current!==identity){reportedPaint.current=identity;onPainted?.()}
    }).catch(error => {
      if (!disposed && error.name !== 'RenderingCancelledException') setError(String(error))
    })
    return () => { disposed = true; render.cancel() }
  }, [pdfPage, scale, size.width, size.height, offset.x, offset.y, identity, crop?.x, crop?.y, width, height])

  const point=(event:React.PointerEvent)=>{
    const rect=wrapper.current!.getBoundingClientRect()
    return {x:Math.max(0,Math.min(1,(event.clientX-rect.left)/width)),y:Math.max(0,Math.min(1,(event.clientY-rect.top)/height))}
  }

  const place=(region:PageRegion)=>({left:`${region.x*100}%`,top:`${region.y*100}%`,width:`${region.width*100}%`,height:`${region.height*100}%`})
  const approximate = precision==='approximate' || marker?.precision==='approximate'
  // On the whole sheet a table cell is a few pixels: below this size a locator ring keeps the marker findable.
  const small = !!highlight && Math.max(highlight.width*width, highlight.height*height) < 32

  return <div ref={wrapper} className="pdf-source" style={{ position: 'relative', width, height, marginLeft: left, marginTop: top }}>
    {!error && !visible && <p role="status" className="pdf-loading">Загружаем страницу…</p>}
    {/* The frame is drawn around the value, never over it; the layer clips at the sheet edge so a marker cannot add scrollbars. */}
    {!error && visible && <div className="cad-marker-layer">
      {marker && markers.map(region=><div key={region.key} className={`cad-highlight cad-marker secondary ${marker.tone} ${region.precision==='approximate'||marker.precision==='approximate'?'approximate':'exact'}`} role="note" aria-label="Ещё одна отметка сравниваемого значения" style={place(region)}/>)}
      {highlight && <div className={`cad-highlight cad-marker ${marker?.tone||'amber'} ${approximate?'approximate':'exact'}${small?' small':''}`} role="note" aria-label={`${approximate?'Приблизительно выделено':'Точно выделено'} в источнике${marker?`: ${marker.label}`:''}`} style={place(highlight)}>
        {approximate&&<span className={`cad-marker-tag${highlight.y*height<48?' below':''}${highlight.x>.8?' end':''}`}>≈ примерно</span>}
      </div>}
    </div>}
    {selecting && selection && <div className="cad-highlight cad-selection" style={place(selection)}/>}
    {error ? <p className="error">Не удалось прочитать PDF: {error}</p> : <canvas ref={canvas}
      aria-label={label || `Лист ${page + 1} исходного PDF`}
      aria-hidden={!visible}
      style={{visibility:visible?'visible':'hidden',cursor:selecting?'crosshair':undefined,touchAction:selecting?'none':undefined}}
      onPointerDown={event => {
        if(selecting){event.currentTarget.setPointerCapture(event.pointerId);selectionStart.current=point(event);return}
        if (event.pointerType === 'touch' || event.button !== 0) return
        const host = wrapper.current?.parentElement
        if (!host) return
        drag.current = { x: event.clientX, y: event.clientY, left: host.scrollLeft, top: host.scrollTop }
        event.currentTarget.setPointerCapture(event.pointerId)
      }}
      onPointerMove={event => {
        if(selecting&&selectionStart.current){const p=point(event),s=selectionStart.current;selectRegion?.({x:Math.min(p.x,s.x),y:Math.min(p.y,s.y),width:Math.abs(p.x-s.x),height:Math.abs(p.y-s.y)});return}
        const host = wrapper.current?.parentElement
        if (drag.current && host) host.scrollTo(drag.current.left + drag.current.x - event.clientX,
          drag.current.top + drag.current.y - event.clientY)
      }}
      onPointerUp={() => { drag.current = null;selectionStart.current=null }}
      onPointerCancel={() => { drag.current = null;selectionStart.current=null }} />}
  </div>
}
